/**
 * Lien marqueur → photos, porté par le marqueur lui-même.
 *
 * Le XData DR_PHOTO_LINK liste les `DiagnosticImage.id` que le marqueur illustre.
 * Il vit à part de DR_META : une version de l'app qui ne le connaît pas réécrit
 * DR_META champ par champ et perdrait un champ ajouté, pas une application XData
 * distincte. Comme DR_META, il suit l'entité à travers le round-trip DWG.
 *
 * Disposition de la chaîne :
 *   [1001] "DR_PHOTO_LINK"
 *   [1071] id d'une DiagnosticImage (une entrée par photo)
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";

export const PHOTO_LINK_XDATA_APP_NAME = "DR_PHOTO_LINK";

const XDATA_APP = 1001;
const XDATA_INT32 = 1071;
/** Borne de lecture : `next()` renvoie une valeur falsy en fin de chaîne. */
const MAX_PHOTOS = 500;

/** `DiagnosticImage.id` portés par le marqueur, triés. Vide s'il n'y en a aucun. */
export function readMarkerPhotoIds(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): number[] {
  const entity = entityId.safeOpenObject(OpenMode.kForRead, false);
  const appStr = new module.OdString(PHOTO_LINK_XDATA_APP_NAME);
  let head: DrawingModule.OdResBuf | null = null;
  try {
    head = entity.xData1(appStr);
  } finally {
    deleteObj(appStr);
    deleteObj(entity);
  }
  if (!head) return [];

  const nodes: DrawingModule.OdResBuf[] = [];
  let cur: DrawingModule.OdResBuf | null = head;
  while (cur && nodes.length <= MAX_PHOTOS) {
    nodes.push(cur);
    cur = cur.next();
  }
  const ids = new Set<number>();
  for (const node of nodes.slice(1)) {
    if (node.restype() === XDATA_INT32) ids.add(node.getInt32());
  }
  for (let i = nodes.length - 1; i >= 0; i--) deleteObj(nodes[i]);
  return [...ids].sort((a, b) => a - b);
}

/** Remplace les photos du marqueur. Une liste vide retire le XData. */
export function writeMarkerPhotoIds(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  photoIds: readonly number[],
): void {
  const entity = entityId.safeOpenObject(
    OpenMode.kForWrite,
    false,
  ) as DrawingModule.OdDbObject;

  // Toute allocation est libérée dans le finally : une exception ne doit jamais
  // laisser l'entité verrouillée en écriture (crash WASM au redraw suivant).
  let appStr: DrawingModule.OdString | null = null;
  let head: DrawingModule.OdResBuf | null = null;
  const nodes: DrawingModule.OdResBuf[] = [];
  try {
    appStr = new module.OdString(PHOTO_LINK_XDATA_APP_NAME);
    entity.database().newRegApp(appStr);

    head = module.OdResBuf.newRb(XDATA_APP);
    head.setString(appStr);

    let last = head;
    for (const id of new Set(photoIds)) {
      const node = module.OdResBuf.newRb(XDATA_INT32);
      node.setInt32(id);
      last.setNext(node);
      nodes.push(node);
      last = node;
    }
    entity.setXData(head);
  } finally {
    for (const node of nodes) deleteObj(node);
    if (head) deleteObj(head);
    if (appStr) deleteObj(appStr);
    deleteObj(entity);
  }
}
